/**
 * SS-3191: Reports › Campaigns, the new report — one row per campaign, a tab per purpose.
 * GET /reports/outreach?purpose=&created_by=all|ai|staff
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Tab,
  TabList,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faEllipsisVertical,
  faEnvelope,
  faCommentSms,
  faPhone,
  faRobot,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { RiSparkling2Fill } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

import { cqAPI, getBid } from "../../../lib/Auth";
import PreviewModal from "../../Campaigns/PreviewModal";
import { getCampaignPreviewHtml } from "../../Campaigns/CampaignConstants";
import {
  createdByLabel,
  formatDate,
  formatLabel,
  formatPercent,
  goalLabel,
  isReviewTab,
  nextSort,
  presetRange,
  purposeLabel,
  rangeParams,
  sortRows,
  tabHasGoals,
  withLocation,
} from "./outreachReportUtils";

const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

export const FORMAT_ICONS = {
  email: faEnvelope,
  sms: faCommentSms,
  phone: faPhone,
  phone_ai: faRobot,
};

export const FormatIcon = ({ row }) => {
  if (row?.drip) {
    return (
      <Tooltip label={formatLabel(row)}>
        <Flex gap={1} align="center">
          {(row.drip.channels || []).map((channel) => (
            <FontAwesomeIcon key={channel} icon={FORMAT_ICONS[channel] || faEnvelope} />
          ))}
          <Text fontSize="xs">Drip · {row.drip.steps} steps</Text>
        </Flex>
      </Tooltip>
    );
  }
  return (
    <Tooltip label={formatLabel(row)}>
      <span aria-label={formatLabel(row)}>
        <FontAwesomeIcon icon={FORMAT_ICONS[row?.format] || faEnvelope} />
        {row?.format === "phone_ai" && <Text as="span" fontSize="xs" ml={1}>AI</Text>}
      </span>
    </Tooltip>
  );
};

const SortHeader = ({ label, sortKey, sort, onSort, isNumeric }) => (
  <Th cursor="pointer" onClick={() => onSort(sortKey)} isNumeric={isNumeric} whiteSpace="nowrap">
    {label}{" "}
    <FontAwesomeIcon
      icon={sort.key !== sortKey ? faSort : sort.direction === "ascending" ? faSortUp : faSortDown}
    />
  </Th>
);

const CreatedBy = ({ row }) =>
  row?.created_by?.type === "ai" ? (
    <Tooltip label="Created by AI">
      <Flex align="center" gap={1}>
        <Icon as={RiSparkling2Fill} boxSize={4} color="purple.400" />
        <Text>AI</Text>
      </Flex>
    </Tooltip>
  ) : (
    <Text>{createdByLabel(row)}</Text>
  );

const GoalCell = ({ row }) => (
  <Flex align="center" gap={1} wrap="wrap">
    <Text>{goalLabel(row.goal)}</Text>
    {row.goal?.estimated && (
      <Tooltip label="Estimated from the campaign's own text when past campaigns were backfilled">
        <Badge colorScheme="gray">estimated</Badge>
      </Tooltip>
    )}
    {row.goal?.type === "email_opened" && (
      <Tooltip label="Some mail apps (Apple Mail) open emails automatically to protect privacy, so open rates run high.">
        <Badge colorScheme="yellow">note</Badge>
      </Tooltip>
    )}
  </Flex>
);

const SettingsCell = ({ row }) => {
  const lines = row.settings_summary || [];
  if (!lines.length) return <Text color="gray.500">—</Text>;
  return (
    <Tooltip label={<Box>{lines.map((line) => <Text key={line}>{line}</Text>)}</Box>}>
      <Text noOfLines={2} maxW="260px">{lines.slice(0, 2).join(" · ")}</Text>
    </Tooltip>
  );
};

const OutreachCell = ({ row }) =>
  row.drip ? (
    <Tooltip label={`${row.messages_sent || 0} messages sent, ${row.calls_made || 0} calls made`}>
      <Text>{row.outreach}</Text>
    </Tooltip>
  ) : (
    <Text>{row.outreach}</Text>
  );

function CampaignRows({ rows, columns, onOpen, onMenu, expanded, setExpanded }) {
  return rows.flatMap((row) => {
    const isGroup = Array.isArray(row.batches) && row.batches.length > 0;
    const isOpen = !!expanded[row._id];
    const main = (
      <Tr key={row._id} cursor="pointer" _hover={{ bg: "gray.50" }} onClick={() => onOpen(row)} data-testid="campaign-row">
        {columns.map((column) => (
          <Td key={column.key} isNumeric={column.isNumeric}>
            {column.key === "name" && isGroup ? (
              <Flex align="center" gap={2}>
                <IconButton
                  size="xs"
                  variant="ghost"
                  aria-label={isOpen ? "Hide batches" : "Show batches"}
                  icon={<FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />}
                  onClick={(event) => {
                    event.stopPropagation();
                    setExpanded({ ...expanded, [row._id]: !isOpen });
                  }}
                />
                <Text>{row.name}</Text>
                <Badge>{row.batches.length} batches</Badge>
              </Flex>
            ) : (
              column.render(row)
            )}
          </Td>
        ))}
        <Td onClick={(event) => event.stopPropagation()}>{onMenu(row)}</Td>
      </Tr>
    );
    if (!isGroup || !isOpen) return [main];
    return [
      main,
      ...row.batches.map((batch) => (
        <Tr key={`${row._id}-${batch._id}`} cursor="pointer" bg="gray.50" onClick={() => onOpen({ ...batch, bid: batch.bid || row.bid })} data-testid="batch-row">
          {columns.map((column) => (
            <Td key={column.key} isNumeric={column.isNumeric} pl={column.key === "name" ? 10 : undefined}>
              {column.key === "name" ? <Text>Batch {batch.batch_index}/{batch.batch_total}</Text> : column.render(batch)}
            </Td>
          ))}
          <Td onClick={(event) => event.stopPropagation()}>{onMenu({ ...batch, bid: batch.bid || row.bid })}</Td>
        </Tr>
      )),
    ];
  });
}

function OutreachReportList({ onShowClassic }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [purposes, setPurposes] = useState([]);
  const [purpose, setPurpose] = useState(null);
  const [createdBy, setCreatedBy] = useState("all");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sort, setSort] = useState({ key: "updated_goal", direction: "descending", clicked: false });
  const latestRequest = useRef(0);
  const [expanded, setExpanded] = useState({});
  const archived = useDisclosure();
  const preview = useDisclosure();
  const [previewContent, setPreviewContent] = useState({ __html: "" });
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    // only the latest request may fill the table (a slow tab must not overwrite the selected one)
    const request = ++latestRequest.current;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ created_by: createdBy, ...(purpose ? { purpose } : {}) });
      const response = await cqAPI.get(`/reports/outreach?${params.toString()}`);
      if (request !== latestRequest.current) return;
      if (response?.type !== "ok") throw new Error(response?.msg || "Could not load the report.");
      const data = response.data || {};
      setPurposes([...new Set(data.purposes || [])]);
      if (!purpose && data.purpose) setPurpose(data.purpose);
      setRows(data.campaigns || []);
    } catch (error) {
      if (request !== latestRequest.current) return;
      setRows([]);
      toast({ status: "error", title: error.message || "Could not load the report." });
    } finally {
      if (request === latestRequest.current) setIsLoading(false);
    }
  }, [createdBy, purpose, toast]);

  useEffect(() => {
    load();
  }, [load]);
  // the tab shown when an action finishes, not the one it started on
  const loadRef = useRef(load);
  loadRef.current = load;
  const latestPreview = useRef(0);

  const showGoals = tabHasGoals(rows);
  const review = isReviewTab(purpose);

  const columns = useMemo(() => {
    const list = [
      { key: "updated_goal", label: "Updated Goal", render: (row) => formatDate(row.updated_goal) },
      { key: "name", label: "Campaign Name", render: (row) => <Text>{row.name}</Text> },
      { key: "format", label: "Format", render: (row) => <FormatIcon row={row} /> },
      { key: "created", label: "Created", render: (row) => formatDate(row.created) },
      { key: "created_by", label: "Created By", render: (row) => <CreatedBy row={row} /> },
    ];
    if (review) {
      list.push(
        { key: "average_score", label: "Average Score", isNumeric: true, render: (row) => (row.average_score ?? "—") },
        { key: "percent_answered", label: "% Answered", isNumeric: true, render: (row) => formatPercent(row.percent_answered) },
      );
    } else if (showGoals) {
      list.push(
        { key: "goal", label: "Goal", render: (row) => <GoalCell row={row} /> },
        { key: "goal_percent", label: "Goal %", isNumeric: true, render: (row) => formatPercent(row.goal_percent) },
      );
    }
    list.push(
      { key: "outreach", label: "Outreach", isNumeric: true, render: (row) => <OutreachCell row={row} /> },
      { key: "settings", label: "Campaign Settings", render: (row) => <SettingsCell row={row} /> },
    );
    return list;
  }, [review, showGoals]);

  const active = useMemo(() => sortRows(rows.filter((row) => !row.report_archived), sort), [rows, sort]);
  const archivedRows = useMemo(() => sortRows(rows.filter((row) => row.report_archived), sort), [rows, sort]);

  // the campaign's own location (a row of a multi-location list carries it), else the current one
  const withBid = (path, row) => withLocation(path, row.bid || getBid());

  const openDetail = (row) => navigate(withBid(`/reports/outreach/${encodeURIComponent(row._id)}`, row));

  const setArchived = async (row, value) => {
    const response = await cqAPI.put(withBid(`/reports/outreach/${encodeURIComponent(row._id)}/archive`, row), { report_archived: value });
    if (response?.type !== "ok") {
      toast({ status: "error", title: response?.msg || "Could not update the report." });
      return;
    }
    loadRef.current();
  };

  const download = async (row, format) => {
    const params = new URLSearchParams({ format, ...rangeParams(presetRange(28)) });
    const url = await cqAPI.downloadFile(withBid(`/reports/outreach/${encodeURIComponent(row._id)}?${params.toString()}`, row));
    if (typeof url !== "string") {
      toast({ status: "error", title: "The download failed." });
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = `${row.name || "campaign"}.${format}`;
    link.click();
  };

  const viewTemplate = async (row) => {
    // only the latest preview asked for may fill the modal
    const request = ++latestPreview.current;
    const show = (html) => request === latestPreview.current && setPreviewContent({ __html: html });
    preview.onOpen();
    setPreviewLoading(true);
    try {
      // the campaign's own location (a row of a multi-location list carries it)
      const response = await cqAPI.get(withBid("/campaigns", row));
      if (response?.type === "error") {
        // the template is read from Patient Outreach, which needs its own permission
        show(escapeHtml(response.msg || "The template could not be loaded."));
        return;
      }
      const id = row.batches?.[0]?._id || row._id;
      const campaign = (response?.data?.campaigns || []).find((c) => String(c._id) === String(id));
      const html = campaign ? await getCampaignPreviewHtml(campaign) : "Template not found.";
      show(html);
    } catch (error) {
      show("Failed to load content.");
    } finally {
      if (request === latestPreview.current) setPreviewLoading(false);
    }
  };

  const rowMenu = (row) => (
    <Menu isLazy>
      <MenuButton as={IconButton} size="sm" variant="ghost" aria-label="Campaign actions" icon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
      <MenuList>
        {row.report_archived ? (
          <MenuItem onClick={() => setArchived(row, false)}>Unarchive</MenuItem>
        ) : (
          <MenuItem onClick={() => setArchived(row, true)}>Archive</MenuItem>
        )}
        <MenuItem onClick={() => download(row, "csv")}>Download CSV</MenuItem>
        <MenuItem onClick={() => download(row, "pdf")}>Download PDF</MenuItem>
        <MenuItem onClick={() => viewTemplate(row)}>View Template</MenuItem>
      </MenuList>
    </Menu>
  );

  const table = (list) => (
    <Box overflowX="auto">
      <Table size="sm">
        <Thead>
          <Tr>
            {columns.map((column) =>
              column.key === "settings" ? (
                <Th key={column.key}>{column.label}</Th>
              ) : (
                <SortHeader
                  key={column.key}
                  label={column.label}
                  sortKey={column.key}
                  sort={sort}
                  isNumeric={column.isNumeric}
                  onSort={(key) => setSort(nextSort(sort, key))}
                />
              ),
            )}
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          <CampaignRows rows={list} columns={columns} onOpen={openDetail} onMenu={rowMenu} expanded={expanded} setExpanded={setExpanded} />
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Text fontSize="xl" fontWeight="bold">Campaigns</Text>
        <Flex gap={2} align="center">
          <Text fontSize="sm">Created By</Text>
          <Select size="sm" w="120px" value={createdBy} onChange={(event) => setCreatedBy(event.target.value)} aria-label="Created By">
            <option value="all">All</option>
            <option value="ai">AI</option>
            <option value="staff">Staff</option>
          </Select>
          <Button size="sm" onClick={onShowClassic}>Show the classic report</Button>
        </Flex>
      </Flex>
      {purposes.length > 0 && (
        <Tabs
          index={Math.max(0, purposes.indexOf(purpose))}
          onChange={(index) => {
            setExpanded({});
            setPurpose(purposes[index]);
          }}
          mb={4}
          isLazy
        >
          <TabList overflowX="auto">
            {purposes.map((p) => (
              <Tab key={p}>{purposeLabel(p)}</Tab>
            ))}
          </TabList>
        </Tabs>
      )}
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {active.length ? table(active) : <Text color="gray.500">No campaigns.</Text>}
          {archivedRows.length > 0 && (
            <Box mt={6}>
              <Button size="sm" variant="ghost" onClick={archived.onToggle} leftIcon={<FontAwesomeIcon icon={archived.isOpen ? faChevronDown : faChevronRight} />}>
                ARCHIVED ({archivedRows.length})
              </Button>
              <Collapse in={archived.isOpen}>{table(archivedRows)}</Collapse>
            </Box>
          )}
        </>
      )}
      <PreviewModal isOpen={preview.isOpen} onClose={preview.onClose} isLoading={previewLoading} previewContent={previewContent} />
    </Box>
  );
}

export default OutreachReportList;
